import React from 'react';

import { useIntl } from 'react-intl';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { SERIES_ORDER, seriesDescKey, seriesLabelKey } from './seriesMetadata';

interface SeriesTierLegendProps {
  /**
   * Keep the three tiers on one line, with each description on hover instead of beside the label.
   *
   * Spelled out, the legend is three long phrases that wrap to two or three lines and push the
   * first device below the fold — which is most of what it costs on a page someone opened to read
   * prices (Michel, 2026-09-15). The ordering screen has room to spell it out and keeps doing so.
   */
  compact?: boolean;
}

export const SeriesTierLegend = ({ compact }: SeriesTierLegendProps) => {
  const intl = useIntl();

  return (
    <div className={`d-flex flex-wrap small ${compact ? 'gap-3 mb-3' : 'gap-4 mb-4'}`}>
      {SERIES_ORDER.map((serie) => {
        const label = intl.formatMessage({ id: seriesLabelKey(serie) });
        const description = intl.formatMessage({ id: seriesDescKey(serie) });
        const swatch = (
          <div
            className={`flex-shrink-0 rounded-1 bg-swatch-color-${serie}`}
            style={{ width: 14, height: 14 }}
          />
        );

        if (compact) {
          return (
            <OverlayTrigger
              key={serie}
              placement="top"
              overlay={<Tooltip id={`series-tier-${serie}`}>{description}</Tooltip>}
            >
              {/* `tabIndex` so the description is reachable without a pointer: a tooltip that only
                  opens on hover is invisible to keyboard and touch. */}
              <span
                className="d-inline-flex align-items-center gap-2"
                tabIndex={0}
                data-test={`series-tier-${serie}`}
              >
                {swatch}
                <strong>{label}</strong>
              </span>
            </OverlayTrigger>
          );
        }

        return (
          <div key={serie} className="d-flex align-items-center gap-2">
            {swatch}
            <span>
              <strong>{label}</strong>
              <span className="text-secondary">
                {' · '}
                {description}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
};
